/**
 * @file chat-window-context.ts
 * @description Контекст окна «Сообщения» (chat-window-design.md §6, §8;
 * SC64): состояние окна, раскладка и `openChat` для любых страниц магазина.
 *
 * Страница не знает, как устроено окно: «Написать клиенту» в заказе зовёт
 * `openChat({ customerId, orderId })`, карточка клиента —
 * `openChat({ customerId, conversationId })`. Вне провайдера (кабинет
 * платформы, тесты страниц) `useChatWindow` отдаёт выключенное окно —
 * кнопки «Написать» просто не показываются.
 */

import { createContext, useContext, type RefObject } from "react";

import type { ChatTab } from "../../lib/api-chat";
import type { ChatCustomerFilter, ChatPresentation, ChatViewport, ChatWindowState } from "./chat-window-state";
import { INITIAL_CHAT_STATE, chatPresentation } from "./chat-window-state";
import type { ChatHint } from "./useChatSummary";

/** Куда открыть окно. Без полей — просто открыть список. */
export interface OpenChatTarget {
  conversationId?: string;
  customerId?: string;
  /** Имя клиента для отбора «Клиент: … ×», если известно заранее. */
  customerName?: string;
  /** Заказ: найти или создать диалог по клиенту и заказу. */
  orderId?: string;
}

export interface ChatWindowContextValue {
  /** Есть право «Переписка с клиентами» — окно и кнопка доступны. */
  enabled: boolean;
  shopId: string;
  slug: string;
  userId: string;
  /** Вход под сотрудником — переписку можно только читать. */
  readOnly: boolean;
  /** Владелец и оператор: вкладки, «Без ответственного», «Передать». */
  canDispatch: boolean;
  canOpenOrders: boolean;
  canOpenCustomers: boolean;
  state: ChatWindowState;
  viewport: ChatViewport;
  presentation: ChatPresentation;
  openChat: (target?: OpenChatTarget) => void;
  close: () => void;
  /** Убрать окно без шагов истории — уход на другую страницу ссылкой из окна. */
  hide: () => void;
  setExpanded: (expanded: boolean) => void;
  /** Выбрать диалог (на телефоне — шаг истории браузера). */
  select: (id: string | null) => void;
  /** Шаг назад на телефоне: диалог → список. */
  back: () => void;
  setTab: (tab: ChatTab) => void;
  setUnassigned: (value: boolean) => void;
  setCustomer: (customer: ChatCustomerFilter | null) => void;
  setSearch: (value: string) => void;
  /** Кнопка чата — сюда возвращается фокус при сворачивании. */
  launcherRef: RefObject<HTMLButtonElement | null>;
  /** Номер запроса «перечитать число на кнопке». */
  summaryTick: number;
  /** Перечитать число на кнопке (после прочтения и действий). */
  refreshSummary: () => void;
  /** Номер версии данных: действия и отправка увеличивают — список перечитывается. */
  dataVersion: number;
  bumpData: () => void;
  /** Непрочитанные диалоги по `/admin/chat/summary` — кнопка и «Диалог не выбран». */
  unread: number;
  /** Подсказка о новом событии над кнопкой. */
  hint: ChatHint | null;
  dismissHint: () => void;
  /** Спокойное сообщение над списком (например, «диалога ещё нет» под сотрудником). */
  notice: string | null;
  dismissNotice: () => void;
}

const noop = () => undefined;

/** Выключенное окно — вне кабинета магазина. */
export const DISABLED_CHAT_WINDOW: ChatWindowContextValue = {
  enabled: false,
  shopId: "",
  slug: "",
  userId: "",
  readOnly: false,
  canDispatch: false,
  canOpenOrders: false,
  canOpenCustomers: false,
  state: INITIAL_CHAT_STATE,
  viewport: "wide",
  presentation: chatPresentation("wide", false),
  openChat: noop,
  close: noop,
  hide: noop,
  setExpanded: noop,
  select: noop,
  back: noop,
  setTab: noop,
  setUnassigned: noop,
  setCustomer: noop,
  setSearch: noop,
  launcherRef: { current: null },
  summaryTick: 0,
  refreshSummary: noop,
  dataVersion: 0,
  bumpData: noop,
  unread: 0,
  hint: null,
  dismissHint: noop,
  notice: null,
  dismissNotice: noop,
};

export const ChatWindowContext = createContext<ChatWindowContextValue>(DISABLED_CHAT_WINDOW);

/** Окно «Сообщения» магазина вкладки. */
export function useChatWindow(): ChatWindowContextValue {
  return useContext(ChatWindowContext);
}
